"use client";

import type * as React from "react";

// TODO: migrate to react aria alternative
import * as SheetPrimitive from "@radix-ui/react-dialog";
import Close24 from "@ui/icons/24x24/close-24.svg";
import { cn } from "@ui/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import { useTranslations } from "next-intl";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

interface SheetOverlayProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> {
  ref?: React.Ref<React.ElementRef<typeof SheetPrimitive.Overlay>>;
}

const SheetOverlay = ({ className, ref, ...props }: SheetOverlayProps) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-darkest data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
);

SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  [
    "fixed z-[999] bg-default",
    "shadow-lg",
    "transition ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  ],
  {
    variants: {
      effect: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
        fade: "inset-0 h-full data-[state=closed]:fade-out data-[state=open]:fade-in",
      },
    },
    defaultVariants: {
      effect: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {
  hideCloseButton?: boolean;
  ref?: React.Ref<React.ElementRef<typeof SheetPrimitive.Content>>;
}

const SheetContent = ({
  effect = "right",
  className,
  children,
  hideCloseButton = false,
  ref,
  ...props
}: SheetContentProps) => {
  const t = useTranslations("ClientKeys.Globals");

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content className={cn(sheetVariants({ effect }), className)} ref={ref} {...props}>
        {children}
        {!hideCloseButton && (
          <SheetPrimitive.Close
            className={cn(
              "absolute right-16 top-20",
              "effect-focus rounded-full",
              "effect-color hover:text-light",
              "disabled:pointer-events-none",
            )}
          >
            <Close24 />
            <span className='sr-only'>{t("close")}</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
};

SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("", className)} {...props} />
);

SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex w-full", className)} {...props} />
);

SheetFooter.displayName = "SheetFooter";

interface SheetTitleProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title> {
  ref?: React.Ref<React.ElementRef<typeof SheetPrimitive.Title>>;
}

const SheetTitle = ({ className, ref, ...props }: SheetTitleProps) => (
  <SheetPrimitive.Title className={cn("f-ui-01", className)} ref={ref} {...props} />
);

SheetTitle.displayName = SheetPrimitive.Title.displayName;

interface SheetDescriptionProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description> {
  ref?: React.Ref<React.ElementRef<typeof SheetPrimitive.Description>>;
}

const SheetDescription = ({ className, ref, ...props }: SheetDescriptionProps) => (
  <SheetPrimitive.Description className={cn("f-description-02 text-light", className)} ref={ref} {...props} />
);

SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
